import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import * as THREE from "three";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  OrbitControls,
  Text,
  Grid,
  Environment,
  Line,
} from "@react-three/drei";
import {
  Box as MuiBox,
  Typography,
  Button,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";

const SCALE = 0.002;

const TYPE_COLORS = {
  GEN: "#2e7d32",
  SWBDS: "#1565c0",
  TRANS: "#6a1b9a",
  SSTR: "#4a148c",
  VFD: "#e65100",
  MOTOR: "#ad1457",
  UPS: "#00838f",
  DBR: "#0077b6",
};

const TYPE_LABELS = {
  GEN: "Generator",
  SWBDS: "Switchboard",
  TRANS: "Transformer",
  SSTR: "Ship Service Transformer",
  VFD: "Variable Frequency Drive",
  MOTOR: "Electric Motor",
  UPS: "Uninterruptible Power Supply",
  DBR: "Dynamic Braking Resistor",
};

const WIRE_LANES = {
  HV: { trayOffset: 0.0, sideOffset: 0.0 },
  LV: { trayOffset: 0.07, sideOffset: 0.12 },
  DC: { trayOffset: 0.14, sideOffset: -0.12 },
  CONTROL: { trayOffset: 0.21, sideOffset: 0.22 },
  GENERAL: { trayOffset: 0.04, sideOffset: -0.06 },
};

const WIRE_LEGEND = [
  { color: "#ff2222", label: "HV / MV Power" },
  { color: "#2266ff", label: "LV Power" },
  { color: "#ff8800", label: "DC Bus" },
  { color: "#ffdd00", label: "Control / UPS" },
  { color: "#00cfff", label: "General" },
];

const SPEC_GROUPS = {
  GEN: {
    "Power & Electrical": [
      "rated_power_kva",
      "rated_power_kw",
      "rated_voltage",
      "rated_current_a",
      "power_factor",
      "frequency",
      "speed_rpm",
      "number_of_poles",
    ],
    Performance: [
      "efficiency_percent",
      "voltage_regulation_percent",
      "transient_voltage_dip_percent",
      "frequency_regulation_percent",
      "subtransient_reactance_xd",
      "transient_reactance_xd",
      "synchronous_reactance_xd",
      "short_circuit_ratio",
      "fault_current_contribution_ka",
      "parallel_operation",
    ],
    Construction: [
      "insulation_class",
      "temperature_rise_k",
      "enclosure_rating",
      "cooling_method",
      "mounting_arrangement",
      "neutral_earthing",
      "excitation_type",
      "avr_type",
    ],
    Standards: ["shock_standard", "emc_standard", "applicable_standards"],
  },
  SWBDS: {
    "Bus & Ratings": [
      "bus_voltage",
      "bus_current_rating_a",
      "frequency",
      "fault_level_ka",
      "fault_duration_s",
      "breaking_capacity_ka",
      "incoming_breaker_rating_a",
      "number_of_incoming_feeders",
      "number_of_outgoing_feeders",
    ],
    Busbar: ["busbar_material", "busbar_rating_a", "busbar_configuration"],
    "Protection & Metering": [
      "protection_standard",
      "form_of_separation",
      "arc_flash_mitigation",
      "protection_relay_type",
      "metering",
    ],
    Construction: [
      "enclosure_rating",
      "cooling_method",
      "mounting_arrangement",
      "neutral_earthing",
    ],
    Standards: ["shock_standard", "emc_standard", "applicable_standards"],
  },
  TRANS: {
    "Power & Voltages": [
      "power_rating_kva",
      "primary_voltage",
      "secondary_voltage",
      "frequency",
      "turns_ratio",
      "primary_current_a",
      "secondary_current_a",
    ],
    Performance: [
      "vector_group",
      "impedance_voltage_percent",
      "no_load_losses_w",
      "full_load_losses_w",
      "no_load_current_percent",
      "tapping_range",
      "fault_current_contribution_ka",
      "short_circuit_withstand_ka",
      "short_circuit_duration_s",
    ],
    Construction: [
      "winding_configuration",
      "insulation_class",
      "temperature_rise_k",
      "cooling_type",
      "enclosure_rating",
    ],
    Standards: ["shock_standard", "emc_standard", "applicable_standards"],
  },
  VFD: {
    "Power & Electrical": [
      "power_rating_kw",
      "input_voltage",
      "output_voltage",
      "input_frequency",
      "output_frequency",
      "dc_bus_voltage",
      "input_current_a",
      "output_current_a",
    ],
    Performance: [
      "efficiency_percent",
      "power_factor",
      "switching_frequency_khz",
      "harmonic_filter",
      "harmonic_distortion_thd",
      "overload_capacity",
    ],
    Construction: [
      "cooling_method",
      "enclosure_rating",
      "insulation_class",
      "mounting_arrangement",
    ],
    "Control & Comms": [
      "braking_method",
      "protection_functions",
      "communication_protocol",
    ],
    Standards: ["shock_standard", "emc_standard", "applicable_standards"],
  },
  MOTOR: {
    "Power & Electrical": [
      "rated_power_kw",
      "rated_voltage",
      "frequency",
      "rated_current_a",
      "starting_current_a",
      "power_factor",
      "efficiency_percent",
    ],
    Mechanical: [
      "rated_speed_rpm",
      "synchronous_speed_rpm",
      "number_of_poles",
      "rated_torque_nm",
      "frame_size",
      "mounting_type",
      "bearing_type",
    ],
    Performance: [
      "efficiency_class",
      "service_factor",
      "duty_cycle",
      "locked_rotor_torque_percent",
      "breakdown_torque_percent",
      "starting_method",
    ],
    Construction: [
      "insulation_class",
      "temperature_rise_k",
      "enclosure_rating",
      "cooling_method",
    ],
    Standards: ["shock_standard", "emc_standard", "applicable_standards"],
  },
  UPS: {
    "Power & Electrical": [
      "power_rating_kva",
      "power_rating_kw",
      "input_voltage",
      "output_voltage",
      "frequency",
      "input_frequency_range",
      "bypass_rating_a",
    ],
    Performance: [
      "topology",
      "efficiency_percent",
      "output_voltage_regulation",
      "output_frequency_regulation",
      "overload_capacity",
      "harmonic_distortion_thd",
      "power_factor_input",
      "power_factor_output",
    ],
    Battery: [
      "battery_type",
      "battery_autonomy_min",
      "battery_voltage",
      "battery_recharge_time_h",
    ],
    "Bypass & Control": [
      "static_bypass",
      "maintenance_bypass",
      "communication_protocol",
    ],
    Construction: [
      "enclosure_rating",
      "cooling_method",
      "mounting_arrangement",
    ],
    Standards: ["shock_standard", "emc_standard", "applicable_standards"],
  },
  DBR: {
    "Power & Electrical": [
      "dc_bus_voltage",
      "peak_braking_power_kw",
      "continuous_braking_power_kw",
      "peak_current_a",
      "continuous_current_a",
      "resistance_value_ohm",
      "resistance_tolerance",
    ],
    "Duty & Thermal": [
      "braking_duration_peak_s",
      "braking_duration_continuous",
      "duty_cycle",
      "energy_per_cycle_kwh",
      "insulation_class",
      "thermal_trip_temperature_c",
      "thermal_trip_reset",
      "temperature_monitoring",
    ],
    Construction: [
      "number_of_resistor_banks",
      "resistor_element_material",
      "enclosure_rating",
      "cooling_method",
      "mounting_arrangement",
    ],
    "Connected Equipment": ["connected_vfd_tag", "connected_motor_tag"],
    Standards: ["shock_standard", "emc_standard", "applicable_standards"],
  },
};

const ENV_KEYS = new Set([
  "ambient_temp_c",
  "relative_humidity_pct",
  "classification_society",
]);

// ══════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════

function formatFieldLabel(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function resolveConnections(nodes, edges) {
  const busIds = new Set(
    nodes
      .filter((n) => n.type === "BUS" || n.type === "busBarNode")
      .map((n) => n.id),
  );
  const nonBusIds = new Set(
    nodes
      .filter((n) => n.type !== "BUS" && n.type !== "busBarNode")
      .map((n) => n.id),
  );
  const adj = {};
  edges.forEach((e) => {
    if (!adj[e.source]) adj[e.source] = [];
    adj[e.source].push(e.target);
  });

  function findRealTargets(nodeId, visited = new Set()) {
    if (visited.has(nodeId)) return [];
    visited.add(nodeId);
    const real = [];
    for (const tgt of adj[nodeId] || []) {
      if (nonBusIds.has(tgt)) real.push(tgt);
      else if (busIds.has(tgt)) real.push(...findRealTargets(tgt, visited));
    }
    return real;
  }

  const resolved = [];
  edges.forEach((e) => {
    if (!nonBusIds.has(e.source)) return;
    if (nonBusIds.has(e.target)) {
      resolved.push({
        source: e.source,
        target: e.target,
        from_node: e.source,
        to_node: e.target,
      });
    } else if (busIds.has(e.target)) {
      findRealTargets(e.target, new Set()).forEach((tgt) => {
        resolved.push({
          source: e.source,
          target: tgt,
          from_node: e.source,
          to_node: tgt,
        });
      });
    }
  });

  const seen = new Set();
  return resolved.filter((c) => {
    const key = `${c.source}->${c.target}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getWireColor(fromType, toType) {
  const pair = [fromType, toType].sort().join("-");
  const DC_PAIRS = new Set(["DBR-VFD"]);
  const HV_PAIRS = new Set([
    "GEN-SWBDS",
    "GEN-SSTR",
    "GEN-TRANS",
    "SSTR-SWBDS",
    "SWBDS-SSTR",
    "SWBDS-TRANS",
    "TRANS-VFD",
  ]);
  const LV_PAIRS = new Set([
    "MOTOR-SWBDS",
    "MOTOR-VFD",
    "SWBDS-UPS",
    "SWBDS-VFD",
  ]);
  if (DC_PAIRS.has(pair)) return "#ff8800";
  if (HV_PAIRS.has(pair)) return "#ff2222";
  if (LV_PAIRS.has(pair)) return "#2266ff";
  if (fromType === "UPS" || toType === "UPS") return "#ffdd00";
  return "#00cfff";
}

function getWireType(fromType, toType) {
  const pair = [fromType, toType].sort().join("-");
  const DC_PAIRS = new Set(["DBR-VFD"]);
  const HV_PAIRS = new Set([
    "GEN-SWBDS",
    "GEN-SSTR",
    "GEN-TRANS",
    "SSTR-SWBDS",
    "SWBDS-SSTR",
    "SWBDS-TRANS",
    "TRANS-VFD",
  ]);
  const LV_PAIRS = new Set([
    "MOTOR-SWBDS",
    "MOTOR-VFD",
    "SWBDS-UPS",
    "SWBDS-VFD",
  ]);
  if (DC_PAIRS.has(pair)) return "DC";
  if (HV_PAIRS.has(pair)) return "HV";
  if (LV_PAIRS.has(pair)) return "LV";
  if (fromType === "UPS" || toType === "UPS") return "CONTROL";
  return "GENERAL";
}

// ══════════════════════════════════════════════════════════════
// SPEC PANEL
// ══════════════════════════════════════════════════════════════

function SpecPanel({ comp, onClose }) {
  // ── All hooks BEFORE early return ─────────────────────────
  // specs and groups must be memoised so downstream useMemo
  // hooks have stable dependencies — CI treats this as an error
  const specs = useMemo(() => comp?.specs || {}, [comp]);

  const groups = useMemo(() => SPEC_GROUPS[comp?.type] || {}, [comp]);

  const color = TYPE_COLORS[comp?.type] || "#546e7a";

  const groupedKeys = useMemo(
    () => new Set(Object.values(groups).flat()),
    [groups],
  );

  const ungrouped = useMemo(
    () =>
      Object.entries(specs).filter(
        ([k, v]) =>
          !groupedKeys.has(k) &&
          !ENV_KEYS.has(k) &&
          v !== null &&
          v !== "" &&
          v !== undefined,
      ),
    [specs, groupedKeys],
  );

  // ── Early return AFTER all hooks ──────────────────────────
  if (!comp) return null;

  return (
    <MuiBox
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        width: 340,
        height: "100vh",
        backgroundColor: "rgba(255,255,255,0.97)",
        borderLeft: "1px solid #ddd",
        boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <MuiBox
        sx={{
          backgroundColor: color,
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <MuiBox>
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontSize: "1rem",
              lineHeight: 1.2,
            }}
          >
            {comp.label}
          </Typography>
          <Typography
            sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.72rem" }}
          >
            {TYPE_LABELS[comp.type] || comp.type}
          </Typography>
        </MuiBox>
        <IconButton
          size="small"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{
            color: "#fff",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </MuiBox>

      {/* Dimensions badge */}
      <MuiBox
        sx={{
          px: 2,
          py: 0.8,
          backgroundColor: "#f5f5f5",
          borderBottom: "1px solid #eee",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
          📐 {comp.width} × {comp.depth} × {comp.height} mm
          &nbsp;&nbsp;|&nbsp;&nbsp; Tier {comp.tier ?? "—"}
        </Typography>
      </MuiBox>

      {/* Scrollable spec body */}
      <MuiBox sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>
        {Object.keys(specs).length === 0 && (
          <Typography
            sx={{ fontSize: "0.75rem", color: "#999", fontStyle: "italic" }}
          >
            No specifications available. Run AI Fill to populate specs.
          </Typography>
        )}

        {Object.entries(groups).map(([groupName, fields]) => {
          const rows = fields
            .map((f) => [f, specs[f]])
            .filter(([, v]) => v !== null && v !== "" && v !== undefined);
          if (rows.length === 0) return null;
          return (
            <MuiBox key={groupName} sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  mb: 0.6,
                }}
              >
                {groupName}
              </Typography>
              {rows.map(([key, val]) => (
                <MuiBox
                  key={key}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    py: 0.35,
                    borderBottom: "1px solid #f0f0f0",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      color: "#666",
                      flexShrink: 0,
                      maxWidth: "48%",
                      lineHeight: 1.3,
                    }}
                  >
                    {formatFieldLabel(key)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      color: "#111",
                      fontWeight: 500,
                      textAlign: "right",
                      lineHeight: 1.3,
                      wordBreak: "break-word",
                    }}
                  >
                    {String(val)}
                  </Typography>
                </MuiBox>
              ))}
            </MuiBox>
          );
        })}

        {ungrouped.length > 0 && (
          <MuiBox sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: "0.65rem",
                fontWeight: 700,
                color,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.6,
              }}
            >
              Additional
            </Typography>
            {ungrouped.map(([key, val]) => (
              <MuiBox
                key={key}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  py: 0.35,
                  borderBottom: "1px solid #f0f0f0",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    color: "#666",
                    flexShrink: 0,
                    maxWidth: "48%",
                    lineHeight: 1.3,
                  }}
                >
                  {formatFieldLabel(key)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    color: "#111",
                    fontWeight: 500,
                    textAlign: "right",
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                  }}
                >
                  {String(val)}
                </Typography>
              </MuiBox>
            ))}
          </MuiBox>
        )}

        <Divider sx={{ mb: 1.5 }} />
        {Array.from(ENV_KEYS).map((k) =>
          specs[k] ? (
            <MuiBox
              key={k}
              sx={{ display: "flex", justifyContent: "space-between", py: 0.3 }}
            >
              <Typography sx={{ fontSize: "0.62rem", color: "#999" }}>
                {formatFieldLabel(k)}
              </Typography>
              <Typography
                sx={{ fontSize: "0.62rem", color: "#666", fontWeight: 500 }}
              >
                {String(specs[k])}
              </Typography>
            </MuiBox>
          ) : null,
        )}
      </MuiBox>
    </MuiBox>
  );
}

// ══════════════════════════════════════════════════════════════
// PARAMETRIC 3D SHAPES
// ══════════════════════════════════════════════════════════════

function GENShape({ w, h, d, color }) {
  return (
    <group>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[w, h * 0.85, d]} />
        <meshStandardMaterial color={color} metalness={0.4} roughness={0.5} />
      </mesh>
      <mesh position={[w * 0.3, h * 0.5, 0]} castShadow>
        <cylinderGeometry args={[w * 0.06, w * 0.06, h * 0.35, 12]} />
        <meshStandardMaterial color="#1a1a1a" metalness={0.8} roughness={0.3} />
      </mesh>
      {[-3, -2, -1, 0, 1, 2, 3].map((i) => (
        <mesh key={i} position={[w * 0.52, i * h * 0.1, 0]} castShadow>
          <boxGeometry args={[w * 0.04, h * 0.05, d * 0.9]} />
          <meshStandardMaterial color={color} metalness={0.5} roughness={0.4} />
        </mesh>
      ))}
      <mesh position={[0, h * 0.1, d * 0.51]} castShadow>
        <boxGeometry args={[w * 0.4, h * 0.25, d * 0.02]} />
        <meshStandardMaterial color="#1a1a2e" metalness={0.2} roughness={0.8} />
      </mesh>
      {[-1, 0, 1].map((i) => (
        <mesh key={i} position={[i * w * 0.12, h * 0.18, d * 0.52]}>
          <sphereGeometry args={[w * 0.025, 8, 8]} />
          <meshStandardMaterial
            color={i === 0 ? "#00ff44" : i === 1 ? "#ffaa00" : "#ff2200"}
            emissive={i === 0 ? "#00ff44" : i === 1 ? "#ffaa00" : "#ff2200"}
            emissiveIntensity={0.9}
          />
        </mesh>
      ))}
    </group>
  );
}

function SWBDSShape({ w, h, d, color }) {
  const sections = Math.max(2, Math.round(w / 0.6));
  const secW = w / sections;
  return (
    <group>
      {Array.from({ length: sections }).map((_, i) => (
        <group key={i} position={[-w / 2 + secW / 2 + i * secW, 0, 0]}>
          <mesh castShadow receiveShadow>
            <boxGeometry args={[secW * 0.95, h, d]} />
            <meshStandardMaterial
              color={color}
              metalness={0.3}
              roughness={0.6}
            />
          </mesh>
          <mesh position={[0, 0, d * 0.51]}>
            <boxGeometry args={[secW * 0.85, h * 0.88, d * 0.02]} />
            <meshStandardMaterial
              color="#0d3a6e"
              metalness={0.1}
              roughness={0.9}
            />
          </mesh>
          {[-2, -1, 0, 1, 2].map((j) => (
            <mesh key={j} position={[0, j * h * 0.13, d * 0.53]}>
              <boxGeometry args={[secW * 0.35, h * 0.05, d * 0.02]} />
              <meshStandardMaterial
                color="#cccccc"
                metalness={0.6}
                roughness={0.3}
              />
            </mesh>
          ))}
          <mesh position={[secW * 0.32, 0, d * 0.53]}>
            <sphereGeometry args={[secW * 0.07, 8, 8]} />
            <meshStandardMaterial
              color="#888"
              metalness={0.7}
              roughness={0.3}
            />
          </mesh>
          <mesh position={[-secW * 0.3, h * 0.38, d * 0.53]}>
            <sphereGeometry args={[secW * 0.05, 8, 8]} />
            <meshStandardMaterial
              color="#00ff44"
              emissive="#00ff44"
              emissiveIntensity={0.9}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function TRANSShape({ w, h, d, color }) {
  return (
    <group>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[w, h * 0.8, d]} />
        <meshStandardMaterial color={color} metalness={0.3} roughness={0.6} />
      </mesh>
      <mesh position={[-w * 0.25, h * 0.46, 0]} castShadow>
        <cylinderGeometry args={[w * 0.06, w * 0.08, h * 0.25, 10]} />
        <meshStandardMaterial color="#dddddd" metalness={0.6} roughness={0.3} />
      </mesh>
      <mesh position={[-w * 0.25, h * 0.6, 0]}>
        <sphereGeometry args={[w * 0.07, 8, 8]} />
        <meshStandardMaterial color="#bbbbbb" metalness={0.7} roughness={0.2} />
      </mesh>
      <mesh position={[w * 0.25, h * 0.44, 0]} castShadow>
        <cylinderGeometry args={[w * 0.05, w * 0.07, h * 0.2, 10]} />
        <meshStandardMaterial color="#aaaaaa" metalness={0.6} roughness={0.3} />
      </mesh>
      <mesh position={[w * 0.25, h * 0.55, 0]}>
        <sphereGeometry args={[w * 0.06, 8, 8]} />
        <meshStandardMaterial color="#999999" metalness={0.7} roughness={0.2} />
      </mesh>
      {[-2, -1, 0, 1, 2].map((i) => (
        <mesh key={i} position={[i * w * 0.18, 0, d * 0.52]} castShadow>
          <boxGeometry args={[w * 0.08, h * 0.72, d * 0.03]} />
          <meshStandardMaterial
            color="#7b1fa2"
            metalness={0.4}
            roughness={0.5}
          />
        </mesh>
      ))}
      <mesh position={[0, -h * 0.28, d * 0.52]}>
        <boxGeometry args={[w * 0.45, h * 0.1, d * 0.02]} />
        <meshStandardMaterial color="#f5f5f5" metalness={0.1} roughness={0.9} />
      </mesh>
    </group>
  );
}

function SSTRShape({ w, h, d, color }) {
  return (
    <group>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[w, h * 0.78, d]} />
        <meshStandardMaterial color={color} metalness={0.3} roughness={0.6} />
      </mesh>
      <mesh position={[-w * 0.25, h * 0.44, 0]} castShadow>
        <cylinderGeometry args={[w * 0.055, w * 0.075, h * 0.22, 10]} />
        <meshStandardMaterial color="#dddddd" metalness={0.6} roughness={0.3} />
      </mesh>
      <mesh position={[-w * 0.25, h * 0.57, 0]}>
        <sphereGeometry args={[w * 0.065, 8, 8]} />
        <meshStandardMaterial color="#bbbbbb" metalness={0.7} roughness={0.2} />
      </mesh>
      <mesh position={[w * 0.25, h * 0.42, 0]} castShadow>
        <cylinderGeometry args={[w * 0.045, w * 0.065, h * 0.16, 10]} />
        <meshStandardMaterial color="#aaaaaa" metalness={0.6} roughness={0.3} />
      </mesh>
      <mesh position={[w * 0.25, h * 0.52, 0]}>
        <sphereGeometry args={[w * 0.055, 8, 8]} />
        <meshStandardMaterial color="#999999" metalness={0.7} roughness={0.2} />
      </mesh>
      {[-1, 0, 1].map((i) => (
        <mesh key={i} position={[i * w * 0.22, 0, d * 0.52]} castShadow>
          <boxGeometry args={[w * 0.09, h * 0.68, d * 0.03]} />
          <meshStandardMaterial
            color="#4a148c"
            metalness={0.4}
            roughness={0.5}
          />
        </mesh>
      ))}
      <mesh position={[0, -h * 0.26, d * 0.52]}>
        <boxGeometry args={[w * 0.5, h * 0.09, d * 0.02]} />
        <meshStandardMaterial color="#f5f5f5" metalness={0.1} roughness={0.9} />
      </mesh>
    </group>
  );
}

function VFDShape({ w, h, d, color }) {
  return (
    <group>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial color={color} metalness={0.35} roughness={0.5} />
      </mesh>
      {[-3, -2, -1, 0, 1, 2, 3].map((i) => (
        <mesh key={i} position={[i * w * 0.12, h * 0.4, d * 0.51]}>
          <boxGeometry args={[w * 0.05, h * 0.09, d * 0.02]} />
          <meshStandardMaterial
            color="#111111"
            metalness={0.5}
            roughness={0.5}
          />
        </mesh>
      ))}
      <mesh position={[0, h * 0.12, d * 0.52]}>
        <boxGeometry args={[w * 0.5, h * 0.22, d * 0.02]} />
        <meshStandardMaterial
          color="#001133"
          metalness={0.1}
          roughness={0.9}
          emissive="#003a6e"
          emissiveIntensity={0.5}
        />
      </mesh>
      <mesh position={[0, h * 0.12, d * 0.53]}>
        <boxGeometry args={[w * 0.42, h * 0.16, d * 0.01]} />
        <meshStandardMaterial
          color="#0066cc"
          emissive="#0044aa"
          emissiveIntensity={0.6}
          transparent
          opacity={0.7}
        />
      </mesh>
      {[-1, 0, 1].map((i) => (
        <mesh key={i} position={[i * w * 0.14, -h * 0.18, d * 0.52]}>
          <sphereGeometry args={[w * 0.038, 8, 8]} />
          <meshStandardMaterial
            color={i === 0 ? "#00ff44" : i === 1 ? "#ffaa00" : "#ff3300"}
            emissive={i === 0 ? "#00ff44" : i === 1 ? "#ffaa00" : "#ff3300"}
            emissiveIntensity={0.9}
          />
        </mesh>
      ))}
      <mesh position={[0, -h * 0.49, 0]}>
        <boxGeometry args={[w * 0.7, h * 0.03, d * 0.6]} />
        <meshStandardMaterial color="#222222" metalness={0.6} roughness={0.4} />
      </mesh>
    </group>
  );
}

function MOTORShape({ w, h, d, color }) {
  return (
    <group>
      <mesh rotation={[0, 0, Math.PI / 2]} castShadow receiveShadow>
        <cylinderGeometry args={[h * 0.44, h * 0.44, w * 0.72, 24]} />
        <meshStandardMaterial color={color} metalness={0.5} roughness={0.4} />
      </mesh>
      {[-1, 1].map((side) => (
        <mesh
          key={side}
          rotation={[0, 0, Math.PI / 2]}
          position={[side * w * 0.37, 0, 0]}
          castShadow
        >
          <cylinderGeometry args={[h * 0.42, h * 0.42, w * 0.06, 24]} />
          <meshStandardMaterial
            color="#444444"
            metalness={0.6}
            roughness={0.4}
          />
        </mesh>
      ))}
      {Array.from({ length: 10 }).map((_, i) => (
        <mesh
          key={i}
          rotation={[0, 0, Math.PI / 2]}
          position={[-w * 0.28 + i * w * 0.062, 0, 0]}
        >
          <cylinderGeometry args={[h * 0.47, h * 0.47, w * 0.018, 24]} />
          <meshStandardMaterial
            color="#333333"
            metalness={0.6}
            roughness={0.3}
          />
        </mesh>
      ))}
      <mesh
        rotation={[0, 0, Math.PI / 2]}
        position={[w * 0.47, 0, 0]}
        castShadow
      >
        <cylinderGeometry args={[h * 0.11, h * 0.11, w * 0.24, 12]} />
        <meshStandardMaterial
          color="#888888"
          metalness={0.85}
          roughness={0.15}
        />
      </mesh>
      <mesh position={[0, h * 0.49, 0]} castShadow>
        <boxGeometry args={[w * 0.28, h * 0.18, d * 0.28]} />
        <meshStandardMaterial color="#222222" metalness={0.4} roughness={0.6} />
      </mesh>
      {[-1, 1].map((side) => (
        <mesh key={side} position={[side * w * 0.26, -h * 0.49, 0]} castShadow>
          <boxGeometry args={[w * 0.14, h * 0.09, d * 1.05]} />
          <meshStandardMaterial
            color="#444444"
            metalness={0.5}
            roughness={0.5}
          />
        </mesh>
      ))}
    </group>
  );
}

function UPSShape({ w, h, d, color }) {
  return (
    <group>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial color={color} metalness={0.3} roughness={0.6} />
      </mesh>
      {[-3, -2, -1, 0, 1, 2].map((i) => (
        <mesh key={i} position={[0, i * h * 0.12, d * 0.51]}>
          <boxGeometry args={[w * 0.82, h * 0.07, d * 0.03]} />
          <meshStandardMaterial
            color="#1a3a1a"
            metalness={0.3}
            roughness={0.7}
          />
        </mesh>
      ))}
      <mesh position={[0, h * 0.38, d * 0.52]}>
        <boxGeometry args={[w * 0.52, h * 0.1, d * 0.02]} />
        <meshStandardMaterial
          color="#001a00"
          emissive="#00cc44"
          emissiveIntensity={0.5}
        />
      </mesh>
      {[-2, -1, 0, 1, 2].map((i) => (
        <mesh key={i} position={[i * w * 0.16, -h * 0.42, d * 0.51]}>
          <boxGeometry args={[w * 0.07, h * 0.06, d * 0.02]} />
          <meshStandardMaterial
            color="#111111"
            metalness={0.5}
            roughness={0.5}
          />
        </mesh>
      ))}
    </group>
  );
}

function DBRShape({ w, h, d, color }) {
  const edgesGeo = useMemo(
    () => new THREE.EdgesGeometry(new THREE.BoxGeometry(w, h, d)),
    [w, h, d],
  );
  return (
    <group>
      <mesh castShadow>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial
          color={color}
          metalness={0.2}
          roughness={0.7}
          transparent
          opacity={0.18}
        />
      </mesh>
      <lineSegments geometry={edgesGeo}>
        <lineBasicMaterial color={color} />
      </lineSegments>
      {[-2, -1, 0, 1, 2].map((row) => (
        <mesh key={row} position={[0, row * h * 0.16, 0]} castShadow>
          <boxGeometry args={[w * 0.82, h * 0.065, d * 0.72]} />
          <meshStandardMaterial
            color="#cc4400"
            metalness={0.5}
            roughness={0.4}
            emissive="#882200"
            emissiveIntensity={0.5}
          />
        </mesh>
      ))}
      {[-2, -1, 0, 1, 2].map((row) => (
        <mesh key={`z${row}`} position={[0, row * h * 0.16, d * 0.37]}>
          <boxGeometry args={[w * 0.75, h * 0.025, d * 0.02]} />
          <meshStandardMaterial
            color="#ff6600"
            emissive="#ff4400"
            emissiveIntensity={0.7}
          />
        </mesh>
      ))}
      <mesh position={[w * 0.38, h * 0.42, d * 0.52]}>
        <sphereGeometry args={[w * 0.07, 8, 8]} />
        <meshStandardMaterial
          color="#ff2200"
          emissive="#ff2200"
          emissiveIntensity={0.8}
        />
      </mesh>
      <mesh position={[0, h * 0.49, 0]}>
        <boxGeometry args={[w * 0.6, h * 0.04, d * 0.4]} />
        <meshStandardMaterial color="#333333" metalness={0.6} roughness={0.4} />
      </mesh>
    </group>
  );
}

function ComponentShape({ type, w, h, d, color }) {
  const props = { w, h, d, color };
  switch (type) {
    case "GEN":
      return <GENShape {...props} />;
    case "SWBDS":
      return <SWBDSShape {...props} />;
    case "TRANS":
      return <TRANSShape {...props} />;
    case "SSTR":
      return <SSTRShape {...props} />;
    case "VFD":
      return <VFDShape {...props} />;
    case "MOTOR":
      return <MOTORShape {...props} />;
    case "UPS":
      return <UPSShape {...props} />;
    case "DBR":
      return <DBRShape {...props} />;
    default:
      return (
        <mesh castShadow receiveShadow>
          <boxGeometry args={[w, h, d]} />
          <meshStandardMaterial color={color} metalness={0.3} roughness={0.6} />
        </mesh>
      );
  }
}

// ══════════════════════════════════════════════════════════════
// COMPONENT 3D
// ══════════════════════════════════════════════════════════════

function Component3D({ comp, onSelect, isSelected }) {
  const x = comp.x * SCALE + (comp.width * SCALE) / 2;
  const y = (comp.height * SCALE) / 2;
  const z = comp.z * SCALE + (comp.depth * SCALE) / 2;
  const w = comp.width * SCALE;
  const h = comp.height * SCALE;
  const d = comp.depth * SCALE;

  const pointerDown = useRef(null);

  const handlePointerDown = useCallback((e) => {
    e.stopPropagation();
    pointerDown.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handlePointerUp = useCallback(
    (e) => {
      e.stopPropagation();
      if (!pointerDown.current) return;
      const dx = Math.abs(e.clientX - pointerDown.current.x);
      const dy = Math.abs(e.clientY - pointerDown.current.y);
      if (dx < 5 && dy < 5) onSelect(comp);
      pointerDown.current = null;
    },
    [comp, onSelect],
  );

  return (
    <group
      position={[x, y, z]}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      {/* Invisible hit area */}
      <mesh visible={false}>
        <boxGeometry args={[w * 1.05, h * 1.05, d * 1.05]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      <ComponentShape type={comp.type} w={w} h={h} d={d} color={comp.color} />

      {isSelected && (
        <mesh>
          <boxGeometry args={[w * 1.06, h * 1.06, d * 1.06]} />
          <meshBasicMaterial
            color="#ffffff"
            transparent
            opacity={0.15}
            wireframe
          />
        </mesh>
      )}

      <Text
        position={[0, h / 2 + 0.16, 0]}
        fontSize={0.09}
        color="#111111"
        anchorX="center"
        anchorY="bottom"
        outlineWidth={0.006}
        outlineColor="#ffffff"
      >
        {comp.label}
      </Text>
      <Text
        position={[0, h / 2 + 0.07, 0]}
        fontSize={0.055}
        color="#444444"
        anchorX="center"
        anchorY="bottom"
      >
        {comp.type}
      </Text>
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
// ROOM
// ══════════════════════════════════════════════════════════════

function Room({ width, depth, height }) {
  const w = width * SCALE;
  const d = depth * SCALE;
  const h = height * SCALE;
  return (
    <group>
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[w / 2, 0, d / 2]}
        receiveShadow
      >
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial
          color="#d8d8d8"
          metalness={0.05}
          roughness={0.9}
        />
      </mesh>
      <Grid
        position={[w / 2, 0.001, d / 2]}
        args={[w, d]}
        cellSize={0.5}
        cellThickness={0.5}
        cellColor="#bbbbbb"
        sectionSize={2}
        sectionColor="#999999"
        fadeDistance={40}
        fadeStrength={1}
      />
      <mesh position={[w / 2, h - 0.08, d / 2]}>
        <boxGeometry args={[w * 0.96, 0.06, 0.18]} />
        <meshStandardMaterial color="#888888" metalness={0.7} roughness={0.3} />
      </mesh>
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
// CABLE RUN
// ══════════════════════════════════════════════════════════════

function CableRun({ from, to, fromType, toType, cableTrayY }) {
  const wireType = getWireType(fromType, toType);
  const lane = WIRE_LANES[wireType];
  const color = getWireColor(fromType, toType);

  const points = useMemo(() => {
    const fx = from.x * SCALE + lane.sideOffset;
    const fz = from.z * SCALE + lane.sideOffset;
    const fy = from.y * SCALE;
    const tx = to.x * SCALE + lane.sideOffset;
    const tz = to.z * SCALE + lane.sideOffset;
    const ty = to.y * SCALE;
    const tY = cableTrayY + lane.trayOffset;
    return [
      [fx, fy, fz],
      [fx, tY, fz],
      [tx, tY, fz],
      [tx, tY, tz],
      [tx, ty, tz],
    ];
  }, [from, to, lane, cableTrayY]);

  return <Line points={points} color={color} lineWidth={4} dashed={false} />;
}

// ══════════════════════════════════════════════════════════════
// POWER FLOW DOTS
// ══════════════════════════════════════════════════════════════

function PowerFlowDots({ from, to, fromType, toType, cableTrayY, frequency }) {
  const wireType = getWireType(fromType, toType);
  const lane = WIRE_LANES[wireType];
  const color = getWireColor(fromType, toType);
  const isDC = wireType === "DC";
  const isHV = wireType === "HV";

  const path = useMemo(() => {
    const fx = from.x * SCALE + lane.sideOffset;
    const fz = from.z * SCALE + lane.sideOffset;
    const fy = from.y * SCALE;
    const tx = to.x * SCALE + lane.sideOffset;
    const tz = to.z * SCALE + lane.sideOffset;
    const ty = to.y * SCALE;
    const tY = cableTrayY + lane.trayOffset;
    return [
      new THREE.Vector3(fx, fy, fz),
      new THREE.Vector3(fx, tY, fz),
      new THREE.Vector3(tx, tY, fz),
      new THREE.Vector3(tx, tY, tz),
      new THREE.Vector3(tx, ty, tz),
    ];
  }, [from, to, lane, cableTrayY]);

  const { segLengths, totalLength } = useMemo(() => {
    const segs = [];
    let total = 0;
    for (let i = 1; i < path.length; i++) {
      const len = path[i].distanceTo(path[i - 1]);
      total += len;
      segs.push(total);
    }
    return { segLengths: segs, totalLength: total };
  }, [path]);

  const getPointAt = useCallback(
    (t) => {
      const target = t * totalLength;
      let prev = 0;
      for (let i = 0; i < segLengths.length; i++) {
        if (target <= segLengths[i]) {
          const segLen = segLengths[i] - prev;
          const alpha = segLen > 0 ? (target - prev) / segLen : 0;
          return path[i].clone().lerp(path[i + 1], alpha);
        }
        prev = segLengths[i];
      }
      return path[path.length - 1].clone();
    },
    [path, segLengths, totalLength],
  );

  const DOT_COUNT = isHV ? 6 : 4;
  const DOT_SIZE = isHV ? 0.055 : 0.042;
  const DOT_SPACING = 1 / DOT_COUNT;
  const BASE_SPEED = isDC
    ? 0.14
    : isHV
      ? frequency === 60
        ? 0.06
        : 0.045
      : frequency === 60
        ? 0.1
        : 0.08;

  const dotRefs = useMemo(
    () => Array.from({ length: DOT_COUNT }, () => React.createRef()),
    [DOT_COUNT],
  );

  const phaseOffsets = useMemo(
    () => Array.from({ length: DOT_COUNT }, (_, i) => i * DOT_SPACING),
    [DOT_COUNT, DOT_SPACING],
  );

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    dotRefs.forEach((ref, i) => {
      if (!ref.current) return;
      if (isDC) {
        const rawT = (((t * BASE_SPEED + phaseOffsets[i]) % 1) + 1) % 1;
        ref.current.position.copy(getPointAt(rawT));
        ref.current.material.opacity = 1.0;
      } else {
        const cycleSpeed = isHV ? frequency * 0.003 : frequency * 0.006;
        const wave = Math.sin(
          t * cycleSpeed * Math.PI * 2 + phaseOffsets[i] * Math.PI * 2,
        );
        ref.current.position.copy(getPointAt((wave + 1) / 2));
        ref.current.material.opacity = 0.3 + Math.abs(wave) * 0.7;
      }
    });
  });

  return (
    <group>
      {dotRefs.map((ref, i) => (
        <mesh key={i} ref={ref}>
          <sphereGeometry args={[DOT_SIZE, 8, 8]} />
          <meshStandardMaterial
            color={color}
            emissive={color}
            emissiveIntensity={1.2}
            transparent
            opacity={1.0}
          />
        </mesh>
      ))}
    </group>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════

export default function SchematicViewer3D() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [animate, setAnimate] = useState(false);
  const [freq, setFreq] = useState(60);
  const [selectedComp, setSelectedComp] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/schematic/load/${id}`);
      const schematic = res.data.schematic;
      const project = res.data.project || {};

      if (!schematic?.nodes) {
        setError("No schematic data found for this project.");
        return;
      }

      const projFreq = project.frequency
        ? String(project.frequency).includes("50")
          ? 50
          : 60
        : 60;
      setFreq(projFreq);

      const connections = resolveConnections(
        schematic.nodes,
        schematic.edges || [],
      );

      const components = schematic.nodes
        .filter((n) => n.type !== "BUS" && n.type !== "busBarNode")
        .map((n) => ({
          id: n.id,
          type: n.data?.componentType || n.type || "GEN",
          label: n.data?.label || n.label || n.id,
          basic_specs: n.data?.specs || n.specs || {},
        }));

      const layoutRes = await api.post("/schematic/generate-3d", {
        project,
        components,
        connections,
      });

      setData({ ...layoutRes.data, connections_raw: connections });
    } catch (err) {
      console.error("❌ 3D load error:", err);
      setError("Failed to load 3D layout. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Loading ───────────────────────────────────────────────
  if (loading)
    return (
      <MuiBox
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#f0f0f0",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: "#005E60" }} />
        <Typography sx={{ color: "#555", fontSize: "0.9rem" }}>
          Generating 3D layout...
        </Typography>
      </MuiBox>
    );

  // ── Error ─────────────────────────────────────────────────
  if (error || !data)
    return (
      <MuiBox
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          backgroundColor: "#f0f0f0",
          gap: 2,
        }}
      >
        <Alert severity="error" sx={{ borderRadius: "8px", maxWidth: 400 }}>
          {error || "No data available."}
        </Alert>
        <MuiBox sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            sx={{ color: "#005E60", borderColor: "#005E60" }}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/project/${id}`)}
            sx={{ color: "#005E60", borderColor: "#005E60" }}
          >
            ← Back to Project
          </Button>
        </MuiBox>
      </MuiBox>
    );

  // ── Build lookup map ──────────────────────────────────────
  const compById = {};
  data.components.forEach((c) => {
    compById[c.id] = c;
  });

  const roomCentreX = (data.room.width * SCALE) / 2;
  const roomCentreZ = (data.room.depth * SCALE) / 2;
  const roomH = data.room.height * SCALE;
  const cableTrayY = roomH - 0.12;

  return (
    <MuiBox
      sx={{
        height: "100vh",
        width: "100%",
        backgroundColor: "#e8e8e8",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ── Back button ─────────────────────────────────── */}
      <MuiBox sx={{ position: "absolute", top: 16, left: 16, zIndex: 10 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(`/project/${id}`)}
          sx={{
            backgroundColor: "#005E60",
            borderRadius: "8px",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: "#004749" },
          }}
        >
          ← Back to Project
        </Button>
      </MuiBox>

      {/* ── Controls + animation toggle ─────────────────── */}
      <MuiBox
        sx={{
          position: "absolute",
          bottom: 16,
          left: 16,
          zIndex: 10,
          backgroundColor: "rgba(255,255,255,0.92)",
          borderRadius: "8px",
          p: 1,
          border: "1px solid #ddd",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Typography sx={{ fontSize: "0.65rem", color: "#555" }}>
          🖱 Drag to rotate · Scroll to zoom · Right-click to pan
        </Typography>
        <Typography sx={{ fontSize: "0.62rem", color: "#888" }}>
          🖱 Click component to inspect specs
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={animate}
              onChange={(e) => setAnimate(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: "#005E60" },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#005E60",
                },
              }}
            />
          }
          label={
            <Typography
              sx={{ fontSize: "0.65rem", color: "#333", fontWeight: 600 }}
            >
              ⚡ Power Flow {animate ? "ON" : "OFF"} ({freq}Hz)
            </Typography>
          }
          sx={{ margin: 0 }}
        />
      </MuiBox>

      {/* ── Legend — shifts left when panel open ────────── */}
      <MuiBox
        sx={{
          position: "absolute",
          top: 16,
          right: selectedComp ? 356 : 16,
          zIndex: 10,
          backgroundColor: "rgba(255,255,255,0.92)",
          borderRadius: "10px",
          p: 1.5,
          minWidth: 210,
          maxWidth: 270,
          border: "1px solid #ddd",
          boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
          transition: "right 0.25s ease",
        }}
      >
        <Typography
          sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#222", mb: 0.5 }}
        >
          ⚡ 3D LAYOUT
        </Typography>
        <Typography sx={{ fontSize: "0.62rem", color: "#777", mb: 1 }}>
          {(data.room.width / 1000).toFixed(1)}m ×{" "}
          {(data.room.depth / 1000).toFixed(1)}m ×{" "}
          {(data.room.height / 1000).toFixed(1)}m
        </Typography>

        {data.components.map((c) => (
          <MuiBox
            key={c.id}
            onClick={() => setSelectedComp(c)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.4,
              cursor: "pointer",
              borderRadius: "4px",
              p: 0.3,
              backgroundColor:
                selectedComp?.id === c.id
                  ? "rgba(0,94,96,0.08)"
                  : "transparent",
              "&:hover": { backgroundColor: "rgba(0,94,96,0.06)" },
            }}
          >
            <MuiBox
              sx={{
                width: 10,
                height: 10,
                borderRadius: "3px",
                backgroundColor: c.color,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{ fontSize: "0.62rem", color: "#444", lineHeight: 1.3 }}
            >
              <strong style={{ color: "#111" }}>{c.label}</strong>
              <br />
              {c.width}×{c.depth}×{c.height}mm
            </Typography>
          </MuiBox>
        ))}

        <MuiBox sx={{ mt: 1, pt: 1, borderTop: "1px solid #eee" }}>
          <Typography
            sx={{
              fontSize: "0.62rem",
              fontWeight: 700,
              color: "#333",
              mb: 0.5,
            }}
          >
            Cable Legend
          </Typography>
          {WIRE_LEGEND.map((w) => (
            <MuiBox
              key={w.color}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}
            >
              <MuiBox
                sx={{
                  width: 22,
                  height: 4,
                  borderRadius: "2px",
                  backgroundColor: w.color,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "0.6rem", color: "#555" }}>
                {w.label}
              </Typography>
            </MuiBox>
          ))}
        </MuiBox>
      </MuiBox>

      {/* ── Spec panel ──────────────────────────────────── */}
      <SpecPanel comp={selectedComp} onClose={() => setSelectedComp(null)} />

      {/* ── Three.js Canvas ─────────────────────────────── */}
      <Canvas
        shadows
        camera={{
          position: [roomCentreX + 8, roomH + 5, roomCentreZ + 14],
          fov: 48,
          near: 0.1,
          far: 1000,
        }}
        style={{
          background:
            "linear-gradient(180deg,#c8d8e8 0%,#e0e8f0 50%,#e8e8e8 100%)",
        }}
      >
        <ambientLight intensity={0.9} color="#ffffff" />
        <directionalLight
          position={[roomCentreX + 8, roomH + 10, roomCentreZ + 6]}
          intensity={1.4}
          castShadow
          shadow-mapSize={[2048, 2048]}
          shadow-camera-far={80}
          shadow-camera-left={-25}
          shadow-camera-right={25}
          shadow-camera-top={25}
          shadow-camera-bottom={-25}
        />
        <directionalLight
          position={[roomCentreX - 6, roomH + 4, roomCentreZ - 4]}
          intensity={0.5}
          color="#ddeeff"
        />
        <pointLight
          position={[roomCentreX, roomH * 0.85, roomCentreZ]}
          intensity={0.4}
          color="#ffffff"
          distance={40}
        />

        <Room
          width={data.room.width}
          depth={data.room.depth}
          height={data.room.height}
        />

        {data.components.map((comp) => (
          <Component3D
            key={comp.id}
            comp={comp}
            onSelect={setSelectedComp}
            isSelected={selectedComp?.id === comp.id}
          />
        ))}

        {data.connections?.map((conn, i) => {
          const fromComp = compById[conn.from_id];
          const toComp = compById[conn.to_id];
          if (!fromComp || !toComp) return null;

          const from = {
            x: fromComp.x + fromComp.width / 2,
            y: fromComp.height,
            z: fromComp.z + fromComp.depth / 2,
          };
          const to = {
            x: toComp.x + toComp.width / 2,
            y: toComp.height,
            z: toComp.z + toComp.depth / 2,
          };

          return (
            <group key={i}>
              <CableRun
                from={from}
                to={to}
                fromType={fromComp.type}
                toType={toComp.type}
                cableTrayY={cableTrayY}
              />
              {animate && (
                <PowerFlowDots
                  from={from}
                  to={to}
                  fromType={fromComp.type}
                  toType={toComp.type}
                  cableTrayY={cableTrayY}
                  frequency={freq}
                />
              )}
            </group>
          );
        })}

        <OrbitControls
          makeDefault
          enableDamping
          dampingFactor={0.06}
          minDistance={2}
          maxDistance={70}
          target={[roomCentreX, roomH * 0.25, roomCentreZ]}
        />
        <Environment preset="apartment" />
      </Canvas>
    </MuiBox>
  );
}
